/**
 * Picklist sort configuration + comparator (BLU-502).
 *
 * Per-location, admin-editable picklist ordering, stored as a JSONB doc at
 * `locations.production_settings.picklist_sort` (written only through the
 * `update_location_production_setting` RPC allowlist). Mirrors
 * `rip-routing.ts` — a pure runtime resolver over an org-supplied JSONB doc,
 * with hand-written TS types AND a separate Zod schema (this duplication is
 * the repo's own pattern for these modules).
 *
 * The comparator built here is the ONLY ordering authority for the batch
 * picklist: `groupPicklist` sorts groups with it, and label print order is
 * DERIVED from the grouped output (`getLabelsInPrintOrder` flat-maps
 * `garmentIds`), so the on-screen sheet, the A4 print, and the reversed
 * thermal label stack cannot diverge.
 *
 * DEFAULT SAFETY: `DEFAULT_PICKLIST_SORT` reproduces the legacy hardcoded
 * comparator (P&P last → size ladder → sku) byte-for-byte. A location with no
 * saved config renders exactly today's order. The size normalizer is OPT-IN
 * via mode 'standard-normalized' and must NEVER run in 'standard' mode —
 * folding it into the default would move `Large` from rank 26 to 4, `One
 * Size` from 26 to 23, and collapse `XXL` onto `2XL`: a wholesale reorder of
 * the printed sheet at every location on deploy day.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Size ladder — moved from apps/frontend/.../utils/size-ordering.ts (deleted
// in BLU-502; this is now the only copy used by the picklist).
//
// NOTE: two OTHER divergent size ladders exist in the repo and stay out of
// scope here (size-ladder consolidation is a separate follow-up):
//   - `apps/frontend/src/lib/variant-sort.ts:2`
//   - `apps/backend/src/modules/production-orders/production-orders.dao.ts`
//     (~:2023, getFilterOptions size ordering)
// ---------------------------------------------------------------------------
/**
 * Standard size order for apparel.
 * Covers letter sizes, numeric sizes, and one-size items.
 *
 * Order rationale:
 * - Letter sizes: XXS to 5XL (smallest to largest)
 * - Numeric sizes: 0 to 20 (dress/pant sizes)
 * - One-size: At the end (least common)
 */
export const SIZE_ORDER = [
    // Letter sizes (most common)
    'XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', '4XL', '5XL',
    // Numeric sizes (dress/pant)
    '0', '2', '4', '6', '8', '10', '12', '14', '16', '18', '20',
    // One-size variants
    'ONE', 'OS', 'OSFA',
];
/**
 * Get sort order for a size.
 * Unknown sizes get high number (sorted after known sizes).
 *
 * Empty strings and unrecognised raw Shopify option text (`Large`, `One
 * Size`, `XXL` variants not in the ladder) all land on the same terminal rank
 * (`SIZE_ORDER.length + 1` = 26) — this rank is authoritative in every size
 * mode, which is why the comparator's empty-last rule does NOT apply to the
 * size field.
 *
 * @param size - Size string (case-insensitive)
 * @returns Numeric order value (lower = first)
 */
export function getSizeOrder(size) {
    const upperSize = size.toUpperCase().trim();
    const index = SIZE_ORDER.indexOf(upperSize);
    return index >= 0 ? index : SIZE_ORDER.length + 1;
}
/** Size codes in the ladder order (XS, S, M, ...), unknown codes after, by name. */
export function compareSizeCodes(a, b) {
    return getSizeOrder(a) - getSizeOrder(b) || a.localeCompare(b);
}
/**
 * Alias-map a raw Shopify size option to its ladder code.
 *
 * ⚠ OPT-IN ONLY: applied when the size key is in `standard-normalized` mode,
 * and for `custom`-mode LIST MATCHING. Never in `standard` — the default
 * config must reproduce the legacy order, where `Large`/`Medium`/`One Size`
 * tie at the terminal rank.
 */
export function normalizeSize(raw) {
    const upper = raw.toUpperCase().trim();
    const alias = SIZE_ALIASES[upper];
    return alias ?? upper;
}
const SIZE_ALIASES = {
    'SMALL': 'S',
    'MEDIUM': 'M',
    'LARGE': 'L',
    'EXTRA LARGE': 'XL',
    'X-LARGE': 'XL',
    'XLARGE': 'XL',
    'XXL': '2XL',
    '2X': '2XL',
    'XXXL': '3XL',
    '3X': '3XL',
    'ONE SIZE': 'OS',
    'ONESIZE': 'OS',
    'OSFA': 'OS',
    'ONE': 'OS',
    'YOUTH SMALL': 'YS',
    'YOUTH MEDIUM': 'YM',
    'YOUTH LARGE': 'YL',
    'YOUTH XL': 'YXL',
    'YOUTH EXTRA LARGE': 'YXL',
};
/**
 * Canonical MATCHING key for text sort values: trim + case-fold.
 *
 * Applied to BOTH the admin's custom `order[]` entries and the row's value, so
 * `natural` / `Natural` / ` Natural` all match one list entry and cannot
 * silently rank nothing. Also the basis for `orderByScope` bucket lookup.
 *
 * ⚠ Two distinct uses, keep them straight:
 *   - EVERY field in `mode: 'custom'` — list matching, the unlisted fallback,
 *     AND `keyEquivalence`. One equivalence basis end to end.
 *   - `color` in `mode: 'alphabetical'` — COLOUR ONLY. See `alphabeticalKey`.
 */
export function normalizeTextKey(raw) {
    return raw.trim().toLowerCase();
}
/**
 * Basis for `mode: 'alphabetical'` on a text field.
 *
 * ⚠ COLOUR CASE-FOLDS. NOTHING ELSE DOES. The asymmetry is deliberate and
 * load-bearing; it is NOT an inconsistency to tidy up:
 *
 *   `productType` keys shipped BEFORE it had any modes, so they are stored
 *   with no `mode` at all, and a RAW `compareText(textValue(...), dir)` is the
 *   order every saved sheet currently prints in. Now that productType has
 *   modes, `sanitizeKey` coerces those stored keys to the field's FIRST
 *   allowed mode ('alphabetical'). Case-folding here would reorder the printed
 *   sheet at every location with a saved productType key, on deploy day, with
 *   nobody touching a setting. The two orders are not close: raw gives
 *   [' Hoodie', 'Golf Shirt', 'hoodie', 'Hoodie', 'HOODIE'], folded gives
 *   ['Golf Shirt', ' Hoodie', 'Hoodie', 'hoodie', 'HOODIE'].
 *
 *   `color` folds because it shipped that way in BLU-502 and its stored keys
 *   already carry an explicit `mode: 'alphabetical'` — nothing to coerce,
 *   nothing to drift.
 *
 * Routing this through a per-FIELD helper rather than a per-MODE branch is
 * what makes the no-drift property structural: `mode: undefined` and
 * `mode: 'alphabetical'` execute literally the same line.
 *
 * Pinned by "alphabetical mode is byte-identical to a mode-less productType
 * key" in picklist-sort.test.ts.
 */
function alphabeticalKey(field, raw) {
    return field === 'color' ? normalizeTextKey(raw) : raw;
}
/**
 * Reproduces the legacy hardcoded comparator exactly: P&P last →
 * `getSizeOrder(size)` ascending → `sku.localeCompare`. Pinned by a unit test
 * against the old comparator's literal output — a location with no saved
 * config must render byte-identically to the pre-BLU-502 sheet.
 */
export const DEFAULT_PICKLIST_SORT = {
    fulfillmentDir: 'asc',
    keys: [
        { field: 'size', dir: 'asc', mode: 'standard' },
        { field: 'sku', dir: 'asc' },
    ],
    bandByFirstKey: false,
};
/** Sanitizer / schema caps — shared so the two stay in lockstep. */
export const PICKLIST_SORT_MAX_KEYS = 5;
export const PICKLIST_SORT_MAX_ORDER_VALUES = 64;
export const PICKLIST_SORT_MAX_VALUE_LENGTH = 100;
/**
 * Cap on `orderByScope` groups per key. Worst-case stored doc is
 * 24 × 64 × 100 chars ≈ 150KB, read on every batch-detail open — a guard rail,
 * not a use case (a real config is ~8 types × ~10 colours ≈ 1KB), and
 * within-tenant self-inflicted, same reasoning as the `order[]` cap.
 */
export const PICKLIST_SORT_MAX_SCOPED_GROUPS = 24;
/**
 * Modes applicable per field; first entry is the field's default mode.
 *
 * ⚠ The FIRST entry is what `sanitizeKey` coerces a stored key with NO `mode`
 * to. Adding modes to a previously mode-less field is therefore a SILENT
 * REORDER of every saved sheet unless that first mode reproduces the mode-less
 * comparison exactly — see `alphabeticalKey`.
 */
const FIELD_MODES = {
    productType: ['alphabetical', 'custom'],
    color: ['alphabetical', 'custom'],
    size: ['standard', 'standard-normalized', 'custom'],
    sku: [],
    productName: [],
};
export const SORT_FIELD_META = {
    productType: {
        label: 'Type / Product',
        modes: FIELD_MODES.productType,
        directionCopy: () => ({ asc: 'A → Z', desc: 'Z → A' }),
    },
    color: {
        label: 'Colour',
        modes: FIELD_MODES.color,
        directionCopy: () => ({ asc: 'A → Z', desc: 'Z → A' }),
    },
    size: {
        label: 'Size',
        modes: FIELD_MODES.size,
        directionCopy: () => ({ asc: 'XS → 5XL', desc: '5XL → XS' }),
    },
    sku: {
        label: 'SKU',
        modes: FIELD_MODES.sku,
        directionCopy: () => ({ asc: 'A → Z', desc: 'Z → A' }),
    },
    productName: {
        label: 'Product name',
        modes: FIELD_MODES.productName,
        directionCopy: () => ({ asc: 'A → Z', desc: 'Z → A' }),
    },
};
/** Extract the raw string value for a field ('' when absent). */
function textValue(row, field) {
    switch (field) {
        case 'productType':
            return row.shopifyProductType ?? '';
        case 'color':
            return row.color ?? '';
        case 'productName':
            return row.productName ?? '';
        case 'sku':
            return row.sku;
        case 'size':
            return row.size;
        default:
            return '';
    }
}
/**
 * Compare two rows on one text key with the empty-last rule: empty/null
 * values sort LAST regardless of direction. Applies to `color`,
 * `productType`, `productName` and `sku` ONLY — `size` is exempt because
 * `getSizeOrder` already assigns empty and unrecognised sizes the same
 * terminal rank (26), and that rank is authoritative in every size mode.
 */
function compareText(a, b, dir) {
    const aEmpty = a === '';
    const bEmpty = b === '';
    if (aEmpty !== bEmpty)
        return aEmpty ? 1 : -1;
    if (aEmpty && bEmpty)
        return 0;
    const cmp = a.localeCompare(b);
    return dir === 'desc' ? -cmp : cmp;
}
/**
 * Compare on a custom-ordered list: listed values always rank BEFORE
 * unlisted, in list order; unlisted values fall through to `fallback`.
 * ⚠ `dir` is intentionally absent — on a `mode: 'custom'` key the admin's
 * list IS the order and direction is ignored (the sanitizer also rewrites
 * `dir` to 'asc' on custom keys).
 */
function compareCustom(aKey, bKey, listIndex, fallback) {
    const ia = listIndex.get(aKey) ?? -1;
    const ib = listIndex.get(bKey) ?? -1;
    if (ia >= 0 || ib >= 0) {
        if (ia < 0)
            return 1;
        if (ib < 0)
            return -1;
        return ia - ib;
    }
    return fallback();
}
/**
 * The value on the basis THIS key's comparator treats as equal.
 *
 * Single source for two things that must never diverge:
 *   - band boundaries (`getSectionKey` on `keys[0]`), and
 *   - `orderByScope` bucket lookup (a child key reading its parent's value).
 *
 * Deriving both from the comparator's own equivalence is what guarantees that
 * two rows ranked contiguously cannot split into two bands, and that a scope
 * bucket matches exactly the rows the parent key grouped together.
 */
function keyEquivalence(key, row) {
    if (key.field === 'size') {
        const mode = key.mode ?? 'standard';
        // 'standard' ranks getSizeOrder(raw), which upper/trims before the ladder
        // lookup — mirror that canonicalization here.
        return mode === 'standard' ? row.size.toUpperCase().trim() : normalizeSize(row.size);
    }
    if (key.mode === 'custom')
        return normalizeTextKey(textValue(row, key.field));
    return alphabeticalKey(key.field, textValue(row, key.field));
}
function buildListIndex(list, matchKey) {
    const listIndex = new Map();
    (list ?? []).forEach((v, i) => {
        const k = matchKey(v);
        if (!listIndex.has(k))
            listIndex.set(k, i);
    });
    return listIndex;
}
/**
 * Comparator for ONE sort key.
 *
 * `scopeKey` is the resolved `scopedBy` ancestor (undefined when the key is
 * unscoped or the scoping was dropped as invalid). It is safe to read the
 * scope bucket from `a` alone: `buildPicklistComparator` is strictly
 * lexicographic, so this comparator only ever runs on two rows that already
 * tied on EVERY earlier key — including the scope key. That invariant is the
 * whole reason per-parent orders can be expressed as a flat key list.
 */
function buildKeyComparator(key, scopeKey) {
    const { field, dir } = key;
    const isSize = field === 'size';
    if (key.mode === 'custom') {
        // Size matches on the alias-mapped ladder code, text fields on the
        // case-folded value.
        const matchKey = isSize ? normalizeSize : normalizeTextKey;
        const defaultIndex = buildListIndex(key.order, matchKey);
        const scopeIndexes = new Map();
        if (scopeKey) {
            for (const [scopeValue, list] of Object.entries(key.orderByScope ?? {})) {
                scopeIndexes.set(normalizeTextKey(scopeValue), buildListIndex(list, matchKey));
            }
        }
        return (a, b) => {
            const aKey = matchKey(textValue(a, field));
            const bKey = matchKey(textValue(b, field));
            // A scope value with its own list REPLACES the default list for its
            // rows; the two are never merged.
            let listIndex = defaultIndex;
            if (scopeKey && scopeIndexes.size > 0) {
                const scoped = scopeIndexes.get(normalizeTextKey(keyEquivalence(scopeKey, a)));
                if (scoped)
                    listIndex = scoped;
            }
            // ⚠ The unlisted fallback compares the NORMALIZED keys, not the raw
            // values. With a raw fallback a third value can sort strictly BETWEEN
            // two rows that share a normalized key — a leading space is not
            // ignorable in the default ICU collation, so
            // ' Hoodie' < 'Golf Shirt' < 'Hoodie'. The band key `hoodie` would then
            // appear twice, non-adjacently, and computeBandStarts would print one
            // product type as two bands with another type wedged between them.
            return compareCustom(aKey, bKey, listIndex, () => isSize ? getSizeOrder(a.size) - getSizeOrder(b.size) : compareText(aKey, bKey, 'asc'));
        };
    }
    if (isSize) {
        const mode = key.mode ?? 'standard';
        const rank = mode === 'standard-normalized'
            ? (s) => getSizeOrder(normalizeSize(s))
            : (s) => getSizeOrder(s);
        return (a, b) => {
            const cmp = rank(a.size) - rank(b.size);
            return dir === 'desc' ? -cmp : cmp;
        };
    }
    // Alphabetical / mode-less text. `alphabeticalKey` case-folds COLOUR ONLY —
    // read its docblock before touching this line.
    return (a, b) => compareText(alphabeticalKey(field, textValue(a, field)), alphabeticalKey(field, textValue(b, field)), dir);
}
/**
 * Build the picklist comparator for a config.
 *
 * Ordering rules:
 * 1. Fulfilment tier first, per `fulfillmentDir` ('asc' = POD first / P&P
 *    last, today's order; 'desc' = P&P first). Locked tier 0 — never a
 *    removable key.
 * 2. Then each key in `keys` order. `dir: 'desc'` negates that key's
 *    comparison, EXCEPT on a `mode: 'custom'` key where the admin's list IS
 *    the order (listed before unlisted, `dir` ignored).
 * 2b. A `mode: 'custom'` key may additionally carry `scopedBy` naming an
 *    EARLIER key, plus `orderByScope` — a per-parent-value order (e.g.
 *    Charcoal after Black in Classic Tees, before Black in Oversized Tees).
 *    Sound because the loop below is strictly lexicographic: a key only runs
 *    on rows that tied on every earlier key, so the parent value is shared.
 * 3. Empty/null values sort last regardless of direction for `color`,
 *    `productType`, `productName` and `sku` ONLY. `size` is exempt —
 *    `getSizeOrder` gives empty and unrecognised sizes the same terminal
 *    rank (26), authoritative in every size mode.
 * 4. Final implicit tiebreak on `sku`. ⚠ This is NOT a total order — two
 *    `firstTwoSegments` SKUs (e.g. `TB-BK`) share one normalized `sku`
 *    across sizes — so determinism for fully-tied rows is delegated to the
 *    DAO's `.order('id')` on `getBatchLabels`.
 */
export function buildPicklistComparator(config) {
    const keyComparators = config.keys.map((key, index) => buildKeyComparator(key, 
    // Only an EARLIER key can be a scope parent. The sanitizer enforces the
    // same rule, so this find() is belt-and-braces for hand-built configs.
    key.scopedBy
        ? config.keys.slice(0, index).find((k) => k.field === key.scopedBy)
        : undefined));
    const fulfillmentSign = config.fulfillmentDir === 'desc' ? -1 : 1;
    return (a, b) => {
        const aIsPAP = a.fulfillmentType === 'P&P' ? 1 : 0;
        const bIsPAP = b.fulfillmentType === 'P&P' ? 1 : 0;
        if (aIsPAP !== bIsPAP)
            return (aIsPAP - bIsPAP) * fulfillmentSign;
        // Tier 0.5: within POD, DTG before DTF (contiguous POD·DTG / POD·DTF
        // picking sections). No-op for PAP rows and for callers that don't
        // project printTechnology.
        if (aIsPAP === 0) {
            const aIsDtf = a.printTechnology === 'DTF' ? 1 : 0;
            const bIsDtf = b.printTechnology === 'DTF' ? 1 : 0;
            if (aIsDtf !== bIsDtf)
                return aIsDtf - bIsDtf;
        }
        for (const compare of keyComparators) {
            const cmp = compare(a, b);
            if (cmp !== 0)
                return cmp;
        }
        return a.sku.localeCompare(b.sku);
    };
}
/**
 * BOUNDARY key of a row's FIRST configured sort key, used for band-header
 * transition detection (`bandByFirstKey`). Bands must key on the COMPOSITE
 * (fulfillmentType, sectionKey) pair — keying on sectionKey alone drops the
 * band at the tier boundary whenever the P&P block opens with the same value
 * the POD block ended on.
 *
 * ⚠ Returns the value on the SAME equivalence basis the first key's
 * comparator sorts on — colour is case-folded (`normalizeTextKey`, both
 * modes) and size is alias-mapped in `standard-normalized`/`custom` modes —
 * otherwise two rows the comparator ranks contiguously (`Black`/`black`,
 * `XXL`/`2XL` normalized) would split into two bands. NOT for display: use
 * `getSectionLabel` for the human-readable band label.
 */
export function getSectionKey(config, row) {
    const first = config.keys[0];
    if (!first)
        return '';
    // `keys[0]` can never be scoped (no earlier key exists, and the sanitizer
    // drops `scopedBy` in that position), so bands are unaffected by scoping.
    return keyEquivalence(first, row);
}
/**
 * Display label for a row's first-key band header — the RAW value as the
 * picker reads it (boundary detection uses `getSectionKey`, which normalizes).
 */
export function getSectionLabel(config, row) {
    const first = config.keys[0];
    if (!first)
        return '';
    if (first.field === 'size')
        return row.size;
    return textValue(row, first.field);
}
// ---------------------------------------------------------------------------
// Sanitizer + Zod schema. The RPC only enforces jsonb_typeof = 'object'
// (GRANT EXECUTE ... TO authenticated + SECURITY INVOKER means an org Admin
// can write ANY object shape straight through PostgREST, bypassing Zod), so
// every READ path must pass the stored value through the sanitizer.
// ---------------------------------------------------------------------------
const SORT_FIELDS = [
    'productType',
    'color',
    'size',
    'sku',
    'productName',
];
function cloneDefault() {
    return {
        fulfillmentDir: DEFAULT_PICKLIST_SORT.fulfillmentDir,
        keys: DEFAULT_PICKLIST_SORT.keys.map((k) => ({ ...k, order: k.order ? [...k.order] : undefined })),
        bandByFirstKey: DEFAULT_PICKLIST_SORT.bandByFirstKey,
    };
}
/**
 * Sanitize one ordered value list (`order` or an `orderByScope` group).
 *
 * Pre-slices before filter/map: the RPC only enforces jsonb_typeof =
 * 'object', so a direct PostgREST write can store an arbitrarily large array —
 * without this, every read iterates the full stored size (within-tenant
 * self-DoS). 4× the cap leaves headroom for invalid entries the filter drops.
 */
function sanitizeOrderList(raw) {
    if (!Array.isArray(raw))
        return undefined;
    return raw
        .slice(0, PICKLIST_SORT_MAX_ORDER_VALUES * 4)
        .filter((v) => typeof v === 'string')
        .map((v) => v.trim())
        .filter((v) => v.length > 0 && v.length <= PICKLIST_SORT_MAX_VALUE_LENGTH)
        .slice(0, PICKLIST_SORT_MAX_ORDER_VALUES);
}
function sanitizeKey(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return null;
    const obj = raw;
    const field = obj.field;
    if (typeof field !== 'string' || !SORT_FIELDS.includes(field))
        return null;
    const typedField = field;
    const allowedModes = FIELD_MODES[typedField];
    let mode;
    if (typeof obj.mode === 'string' && allowedModes.includes(obj.mode)) {
        mode = obj.mode;
    }
    else if (allowedModes.length > 0) {
        mode = allowedModes[0];
    }
    let dir = obj.dir === 'desc' ? 'desc' : 'asc';
    // ⚠ On a custom key the list IS the order — normalize dir to 'asc' so a
    // stored desc can never resurface if the mode is later flipped.
    if (mode === 'custom')
        dir = 'asc';
    let order;
    if (mode === 'custom' && Array.isArray(obj.order)) {
        order = sanitizeOrderList(obj.order);
    }
    // ⚠ `scopedBy` validity also depends on POSITION (the scope field must
    // appear EARLIER in the final key list), which a per-key pass cannot know.
    // That half is enforced by the second pass in sanitizePicklistSortConfig.
    let scopedBy;
    let orderByScope;
    if (mode === 'custom') {
        if (typeof obj.scopedBy === 'string' &&
            SORT_FIELDS.includes(obj.scopedBy) &&
            obj.scopedBy !== typedField) {
            scopedBy = obj.scopedBy;
        }
        if (scopedBy &&
            typeof obj.orderByScope === 'object' &&
            obj.orderByScope !== null &&
            !Array.isArray(obj.orderByScope)) {
            const groups = {};
            const seenScopeKeys = new Set();
            // Same pre-slice reasoning as sanitizeOrderList: a direct PostgREST
            // write can store an arbitrarily wide object.
            for (const [rawScopeKey, rawList] of Object.entries(obj.orderByScope).slice(0, PICKLIST_SORT_MAX_SCOPED_GROUPS * 4)) {
                if (seenScopeKeys.size >= PICKLIST_SORT_MAX_SCOPED_GROUPS)
                    break;
                const scopeKey = rawScopeKey.trim();
                if (scopeKey.length === 0 || scopeKey.length > PICKLIST_SORT_MAX_VALUE_LENGTH)
                    continue;
                // Dedupe on the MATCHING key so 'Classic Tee' and 'classic tee'
                // cannot both be stored (the comparator would only ever find one).
                const canonical = normalizeTextKey(scopeKey);
                if (seenScopeKeys.has(canonical))
                    continue;
                const list = sanitizeOrderList(rawList);
                if (!list || list.length === 0)
                    continue;
                seenScopeKeys.add(canonical);
                // Store the key as the admin typed it — the settings UI renders it as
                // a group heading. Matching normalizes on read.
                groups[scopeKey] = list;
            }
            if (seenScopeKeys.size > 0)
                orderByScope = groups;
        }
    }
    const key = { field: typedField, dir };
    if (mode !== undefined)
        key.mode = mode;
    if (order !== undefined)
        key.order = order;
    if (scopedBy !== undefined)
        key.scopedBy = scopedBy;
    if (orderByScope !== undefined)
        key.orderByScope = orderByScope;
    return key;
}
/**
 * Zod schema for the tRPC write path (`setPicklistSortConfig.input`).
 * Carries the SAME caps as `sanitizePicklistSortConfig` — keep the two in
 * lockstep. Field/mode applicability is intentionally looser here (the
 * sanitizer runs on every read and coerces mode to the field's default), but
 * structural caps are enforced at the boundary so an oversized doc is
 * rejected loudly rather than silently truncated.
 */
export const PicklistSortKeySchema = z.object({
    field: z.enum(['productType', 'color', 'size', 'sku', 'productName']),
    dir: z.enum(['asc', 'desc']),
    mode: z.enum(['alphabetical', 'standard', 'standard-normalized', 'custom']).optional(),
    order: z
        .array(z.string().trim().min(1).max(PICKLIST_SORT_MAX_VALUE_LENGTH))
        .max(PICKLIST_SORT_MAX_ORDER_VALUES)
        .optional(),
    scopedBy: z.enum(['productType', 'color', 'size', 'sku', 'productName']).optional(),
    orderByScope: z
        .record(z.string().trim().min(1).max(PICKLIST_SORT_MAX_VALUE_LENGTH), z.array(z.string().trim().min(1).max(PICKLIST_SORT_MAX_VALUE_LENGTH)).max(PICKLIST_SORT_MAX_ORDER_VALUES))
        .refine((groups) => Object.keys(groups).length <= PICKLIST_SORT_MAX_SCOPED_GROUPS, {
        message: `At most ${PICKLIST_SORT_MAX_SCOPED_GROUPS} scoped orders per key`,
    })
        .optional(),
});
export const PicklistSortConfigSchema = z.object({
    fulfillmentDir: z.enum(['asc', 'desc']),
    keys: z.array(PicklistSortKeySchema).max(PICKLIST_SORT_MAX_KEYS),
    bandByFirstKey: z.boolean(),
});
/**
 * Sanitize an untrusted/raw stored value into a safe `PicklistSortConfig`.
 * Drops unknown fields, dedupes repeated sort fields (first occurrence
 * wins), caps `keys` at 5, each `order[]` at 64 entries and `orderByScope` at
 * 24 groups, normalizes `dir` to 'asc' on any `mode: 'custom'` key, drops
 * `scopedBy`/`orderByScope` unless the scope field appears earlier in the
 * final key list, and falls back to `DEFAULT_PICKLIST_SORT` on anything
 * structurally malformed (including absent — an unset location gets today's
 * order). Returns a FRESH object.
 */
export function sanitizePicklistSortConfig(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return cloneDefault();
    const obj = raw;
    if (!Array.isArray(obj.keys))
        return cloneDefault();
    const keys = [];
    const seenFields = new Set();
    for (const rawKey of obj.keys) {
        if (keys.length >= PICKLIST_SORT_MAX_KEYS)
            break;
        const key = sanitizeKey(rawKey);
        if (!key || seenFields.has(key.field))
            continue;
        seenFields.add(key.field);
        keys.push(key);
    }
    // ⚠ SECOND PASS — position-dependent, so it cannot live in sanitizeKey.
    // `scopedBy` must name a field appearing EARLIER in the final key list; a
    // scope key that is later, missing, or dropped by the dedupe/5-key cap would
    // otherwise silently order by a value the comparator has not yet grouped on.
    // The key degrades to its flat `order[]` — coherent, never broken. This is
    // what makes reordering keys in the UI safe.
    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        if (!key?.scopedBy)
            continue;
        const scopeIsEarlier = keys.slice(0, i).some((k) => k.field === key.scopedBy);
        if (!scopeIsEarlier) {
            delete key.scopedBy;
            delete key.orderByScope;
        }
    }
    return {
        fulfillmentDir: obj.fulfillmentDir === 'desc' ? 'desc' : 'asc',
        keys,
        bandByFirstKey: obj.bandByFirstKey === true,
    };
}
